'use client';

import { useState } from 'react';

import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';

import { CardsProps } from './types';

const listItemStyles = {
  p: 'inline text-text-list',
  em: 'not-italic text-sm',
};

const BOLD_PRICE_PATTERN = /^(.*?)(\*\*(.+?)\*\*)(.*)$/;

export default function Cards({
  title,
  testType,
  price,
  description = [],
  gender,
  referral,
  subDescription = [],
  notes = [],
  hasButton = false,
  defaultOpen = false,
  onEnquire = () => {},
  categoryLabel = 'Scan',
  includedLabel = 'Included',
}: CardsProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isNumber = !isNaN(Number(price));
  const displayPrice =
    isNumber && price ? Number(price).toLocaleString() : price;

  return (
    <div
      data-card
      className="mb-3 flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-md"
    >
      <p className="text-theme-brand-blue text-sm font-bold">{categoryLabel}</p>

      <p className="text-text-strong mt-2 text-2xl font-bold">{title}</p>

      <p className="text-text-list font-regular mt-1 text-sm">{testType}</p>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="flex items-start gap-3">
          <div className="relative mt-0.5 h-5 w-5 shrink-0">
            <R2Image
              path="/images/fibroscan/no-referral-required.svg"
              alt="Referral"
              objectFit="contain"
            />
          </div>

          <div className="flex flex-col">
            <div className="text-theme-brand-blue text-base font-semibold">
              Referral
            </div>

            <div className="text-text-disabled-muted font-regular text-base">
              {referral}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="relative mt-0.5 h-5 w-5 shrink-0">
            <R2Image
              path="/images/healthpackages/gender-icon.svg"
              alt="Gender"
              width={20}
              height={20}
              className="shrink-0 object-contain"
            />
          </div>

          <div className="flex flex-col">
            <div className="text-theme-brand-blue text-base font-semibold">
              Gender
            </div>

            <div className="text-text-disabled-muted font-regular text-base">
              {gender}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`flex items-center justify-between ${open ? 'pt-4' : 'mt-4'}`}
      >
        <div className="flex items-center">
          <div className="relative h-5 w-5">
            <R2Image
              path="/images/fibroscan/File-text-icon.svg"
              alt={includedLabel}
              objectFit="contain"
            />
          </div>

          <div className="text-theme-brand-blue ml-2 text-base font-semibold">
            {includedLabel}
          </div>
        </div>
      </div>

      <div>
        <div className="mt-2">
          {description.length > 0 && (
            <ul
              className="font-regular text-text-list ml-2 space-y-1.5 text-base"
              data-testid="description-list"
            >
              {description.map((item, i) => {
                const priceMatch = item.match(BOLD_PRICE_PATTERN);

                return (
                  <li key={i} className="mt-1.5 flex items-start gap-2">
                    <div className="relative mt-0.5 h-5 w-5 shrink-0">
                      <R2Image
                        path="/images/fibroscan/check-icon.svg"
                        alt="check"
                        objectFit="contain"
                      />
                    </div>
                    {priceMatch ? (
                      <span className="flex w-full items-end justify-between gap-2">
                        <span>
                          {priceMatch[1]}
                          {priceMatch[4]}
                        </span>
                        <strong className="text-theme-primary ml-2 shrink-0">
                          {priceMatch[3]}
                        </strong>
                      </span>
                    ) : (
                      <RichText className="contents" styles={listItemStyles}>
                        {item}
                      </RichText>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {subDescription.length > 0 && (
            <div className="mt-2 ml-2">
              <ul
                className="font-regular text-text-list space-y-1.5 text-base"
                data-testid="sub-description-list"
              >
                {subDescription.map((item, i) => (
                  <li key={i} className={i > 0 && !open ? 'hidden' : ''}>
                    {i === 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="relative h-5 w-5 shrink-0">
                          <R2Image
                            path="/images/fibroscan/check-icon.svg"
                            alt="check"
                            objectFit="contain"
                          />
                        </div>
                        <RichText className="contents" styles={listItemStyles}>
                          {item}
                        </RichText>
                      </div>
                    ) : (
                      <span className="ml-6 flex items-center gap-2">
                        <span className="bg-surface-list-marker h-1.5 w-1.5 shrink-0 rounded-full" />
                        <RichText className="contents" styles={listItemStyles}>
                          {item}
                        </RichText>
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {subDescription.length > 1 && (
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className="font-regular text-theme-primary mt-3 flex cursor-pointer items-center gap-2 text-base"
                >
                  {open ? 'View Less' : 'View All'}
                  <svg
                    className={`h-4 w-4 transition-transform duration-300 ${
                      open ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {notes.length > 0 && (
          <div className="bg-surface-light mt-4 rounded-lg p-4">
            <div className="mb-4 flex items-center">
              <div className="relative h-5 w-5">
                <R2Image
                  path="/images/fibroscan/grey-info-icon.svg"
                  alt="Info"
                  objectFit="contain"
                />
              </div>

              <p className="text-text-list ml-2 text-base font-bold">
                Please note:
              </p>
            </div>

            <ul className="font-regular text-text-list ml-2 list-disc space-y-1 pl-5 text-sm">
              {notes.map((note, i) => {
                const boldMatch = note.match(/^(.*?)(\*\*(.+?)\*\*)(.*)$/);

                if (boldMatch) {
                  return (
                    <li key={i}>
                      {boldMatch[1]}
                      <strong className="text-theme-primary text-[20px] font-bold">
                        {boldMatch[3]}
                      </strong>
                      {boldMatch[4]}
                    </li>
                  );
                }

                return <li key={i}>{note}</li>;
              })}
            </ul>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-4">
          {hasButton && (
            <button
              type="button"
              onClick={onEnquire}
              className="bg-theme-primary font-regular cursor-pointer rounded-lg px-7 py-4 text-base text-white transition hover:bg-indigo-800 active:bg-indigo-900"
            >
              Enquire Now
            </button>
          )}

          {isNumber ? (
            <div className="ml-2 flex items-center gap-2 whitespace-nowrap">
              <div className="text-theme-primary text-[26px] font-bold">
                £{displayPrice}
              </div>
            </div>
          ) : (
            <div className="text-theme-primary text-[26px] font-bold whitespace-nowrap">
              {displayPrice}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
